import React, { useEffect, useState } from 'react';
import { Search, Building2, Users } from 'lucide-react';
import { organizationsService } from '../services/organizations';
import { OrganizationWithStats, OrganizationStatus } from '../types';

const MINT = '#2f9e5a';
const GOLD = '#f39200';
const CORAL = '#af3216';

export const Organizations: React.FC = () => {
  const [organizations, setOrganizations] = useState<OrganizationWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrganizationStatus | ''>('');

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const data = await organizationsService.getAllWithStats();
      setOrganizations(data);
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = !searchQuery ||
      org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.contactEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      org.contactName?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = !statusFilter || org.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-ink">
          Organisations
        </h1>
        <p className="text-ink-soft mt-1">
          Vue d'ensemble de vos organisations clientes
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-soft" />
          <input
            type="text"
            placeholder="Rechercher une organisation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as OrganizationStatus | '')}
          className="bg-white border border-cream-high rounded-xl text-ink text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/20"
        >
          <option value="">Tous les statuts</option>
          <option value="active">Actif</option>
          <option value="inactive">Inactif</option>
        </select>
      </div>

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredOrganizations.length === 0 ? (
          <div className="col-span-full bg-white rounded-2xl shadow-ambient text-center py-14 px-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gold-container/50 flex items-center justify-center">
              <Building2 size={32} className="text-gold" />
            </div>
            <h3 className="text-lg font-semibold text-ink mb-1">
              Aucune organisation trouvée
            </h3>
            <p className="text-sm text-ink-soft max-w-sm mx-auto">
              Modifiez vos critères de recherche ou ajoutez des organisations depuis Supabase.
            </p>
          </div>
        ) : (
          filteredOrganizations.map((org) => (
            <OrganizationCard key={org.id} organization={org} mint={MINT} gold={GOLD} coral={CORAL} />
          ))
        )}
      </div>
    </div>
  );
};

const OrganizationCard: React.FC<{
  organization: OrganizationWithStats;
  mint: string;
  gold: string;
  coral: string;
}> = ({ organization, mint, gold, coral }) => {
  const statusStyles: Record<string, string> = {
    active: 'bg-mint-container text-mint rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.05em]',
    inactive: 'bg-cream-high text-ink-soft rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.05em]',
  };

  const statusLabels: Record<string, string> = {
    active: 'Actif',
    inactive: 'Inactif',
  };

  const progressColor = (pct: number) =>
    pct >= 80 ? mint : pct >= 30 ? gold : coral;

  return (
    <div className="bg-white shadow-ambient rounded-2xl p-6 transition-all duration-300 hover:shadow-ambient-lg hover:-translate-y-1">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {organization.logoUrl ? (
            <img
              src={organization.logoUrl}
              alt={organization.name}
              className="w-12 h-12 rounded-xl object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-lavender-container flex items-center justify-center">
              <Building2 className="w-6 h-6 text-lavender" />
            </div>
          )}
          <div>
            <h3 className="font-bold text-ink">
              {organization.name}
            </h3>
            <span className={statusStyles[organization.status] || statusStyles.inactive}>
              {statusLabels[organization.status] || organization.status}
            </span>
          </div>
        </div>
      </div>

      {organization.contactEmail && (
        <p className="text-sm text-ink-soft mb-2">{organization.contactEmail}</p>
      )}
      {organization.contactName && (
        <p className="text-sm text-ink-soft mb-4">{organization.contactName}</p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-cream-high">
        <div className="flex items-center gap-2 text-sm text-ink-soft">
          <Users className="w-4 h-4" />
          <span>{organization.learnersCount} apprenants</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-cream-high rounded-full">
            <div
              className="h-2 rounded-full transition-all duration-700"
              style={{
                width: `${organization.completionRate}%`,
                backgroundColor: progressColor(organization.completionRate),
              }}
            />
          </div>
          <span className="text-sm text-ink-soft">
            {organization.completionRate}%
          </span>
        </div>
      </div>
    </div>
  );
};
